import * as LucideIcons from 'lucide-react';
import { Sparkles } from 'lucide-react';
import { useQuickEdit } from '@/hooks/useQuickEdit';
import { useHomepageSectionMutation } from '@/hooks/useHomepageSectionMutation';
import QuickEditInline from '@/components/quickedit/QuickEditInline';

interface ValuePropositionsProps {
  section: any;
}

function resolveIcon(name: string | undefined, className: string) {
  const Icon = name ? ((LucideIcons as any)[name] as React.ComponentType<{ className?: string }> | undefined) : undefined;
  const Resolved = Icon ?? Sparkles;
  return <Resolved className={className} />;
}

export default function ValuePropositions({ section }: ValuePropositionsProps) {
  const items: any[] = section.content?.items ?? [];
  const headingId = `value-props-heading-${section.id}`;

  const { isActive } = useQuickEdit();
  const mutation = useHomepageSectionMutation(section.id);

  if (items.length === 0) return null;

  return (
    <section aria-labelledby={headingId} className="bg-cream border-y border-sage-200">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <h2 id={headingId} className="sr-only">
          {isActive ? (
            <QuickEditInline
              value={section.title || 'Why Choose Us'}
              onSave={(val) => mutation.mutateAsync({ title: val, content: section.content })}
              label="Value propositions title"
              as="span"
            />
          ) : (section.title || 'Why Choose Us')}
        </h2>
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 list-none p-0">
          {items.map((item: any, i: number) => (
            <li key={i} className="text-center">
              <div className="inline-flex items-center justify-center text-sage-600 mb-3" aria-hidden="true">
                {resolveIcon(item.icon, 'h-8 w-8')}
              </div>
              <h3 className="font-display text-base font-semibold text-sage-900">
                {isActive ? (
                  <QuickEditInline
                    value={item.title}
                    onSave={(val) => {
                      const updated = [...items];
                      updated[i] = { ...updated[i], title: val };
                      return mutation.mutateAsync({ content: { ...section.content, items: updated } });
                    }}
                    label={`Value proposition ${i + 1} title`}
                    as="span"
                    className="font-display text-base font-semibold text-sage-900"
                  />
                ) : item.title}
              </h3>
              <p className="mt-1 text-sm text-sage-600">
                {isActive ? (
                  <QuickEditInline
                    value={item.description}
                    onSave={(val) => {
                      const updated = [...items];
                      updated[i] = { ...updated[i], description: val };
                      return mutation.mutateAsync({ content: { ...section.content, items: updated } });
                    }}
                    label={`Value proposition ${i + 1} description`}
                    as="span"
                    className="text-sm text-sage-600"
                  />
                ) : item.description}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
